import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Input,
    FormGroup,
    Label,
} from 'reactstrap';
import '../Daylight.css';
import Select from 'react-select'; // TODO: is this really needed? replace
import { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../context';
import hasPermission from '../../../Shared/hasPermission';
import Fuse from 'fuse.js';
import debounce from 'lodash.debounce';
import { DaylightContext } from '../DaylightCodeMapperModal/DaylightCodeMapper';

const DaylightAddMappingModal = ({ practiceId }) => {
    const dispatch = useDispatch();

    const isOpen = useSelector((state) => state.daylight.isAddMappingModalOpen);
    const mappings = useSelector(
        (state) => state.daylight.actionTargetCode?.mappings,
        (left, right) => mappingsEquality(left, right, isOpen)
    );

    // this is ~28,951 items long -_-
    // all data syncs are paused and no actions are getting fired so this is fine for now
    // TODO: we slice off the first 100 filtered so modify the endpoint to accept a mapping ID and text filter,
    // then return 100 from the server then put this into HTTP requests
    const incomingNeoCodes = useSelector((state) =>
        mappings ? state.daylight.incomingNeoCodes : []
    );

    const selectedCode = useSelector(
        (state) => state.daylight.actionTargetCode,
        shallowEqual
    );

    const permissions = useSelector(
        (state) => state.auth.permissions,
        shallowEqual
    );
    const canWriteComments = hasPermission(
        'CodeMapsCommentsWrite',
        permissions
    );

    const { partnerId } = useContext(DaylightContext);

    const [selectedMapping, setSelectedMapping] = useState(null);
    const [commentsText, setCommentsText] = useState('');
    //handle searching
    const [submitting, setSubmitting] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [filteredNeoCodes, setFilteredNeoCodes] = useState(incomingNeoCodes);

    useEffect(() => {
        const fuseOptions = {
            keys: [
                { name: 'code', weight: 1.0 },
                { name: 'label', weight: 0.5 },
            ],
            location: 50,
            distance: 200,
            threshold: 0.5,
        };

        const fuse = new Fuse(incomingNeoCodes, fuseOptions);

        const debouncedSearch = debounce((value) => {
            const results = value
                ? fuse.search(value)
                : incomingNeoCodes.map((code) => ({ item: code }));
            setFilteredNeoCodes(results.map((result) => result.item));
        }, 300);

        debouncedSearch(inputValue);
        return () => debouncedSearch.cancel();
    }, [incomingNeoCodes, inputValue]);

    const handleInputChange = (value) => {
        setInputValue(value);
    };

    const escapeRegExp = (string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    const filteredOptions = useMemo(() => {
        if (inputValue === '') {
            return [
                ...filteredNeoCodes
                    // Changed slice from 100 to 50 because no one looks at it anyway.
                    .slice(0, 50)
                    // No one looks at the unfiltered list because there are 29 thousand codes.
                    // We were filtering a handful of codes out of the 29K codes on most every render.
                    // I put the filter here just on the astronimcal chance it is in first 50,
                    // -to prevent error handling in submit function.
                    .filter(
                        (x) =>
                            mappings?.filter(
                                (y) => y.taxonomyCodeNodeGuid === x.guid
                            ).length === 0
                    )
                    .map((x) => {
                        return { label: `[${x.code}] ${x.label}`, value: x };
                    }),
                {
                    label: 'Search to see more codes...',
                    value: null,
                    isDisabled: true,
                },
            ];
        }
        const escapedInputValue = escapeRegExp(inputValue);
        const regByInclusion = new RegExp(escapedInputValue, 'i');
        return [
            ...filteredNeoCodes
                .filter(
                    (x) =>
                        (regByInclusion.test(x.label) ||
                            regByInclusion.test(x.code)) &&
                        mappings?.filter(
                            (y) => y.taxonomyCodeNodeGuid === x.guid
                        ).length === 0
                )
                .slice(0, 100)
                .map((x) => {
                    return { label: `[${x.code}] ${x.label}`, value: x };
                }),
            {
                label: 'Search to see more codes...',
                value: null,
                isDisabled: true,
            },
        ];
        // eslint-disable-next-line
    }, [inputValue, filteredNeoCodes, mappings?.length]);

    const confirm = () => {
        setSubmitting(true);

        const payload = {
            taxonomyCodeNodeGuid: selectedMapping?.guid,
            taxonomyCodeNodeLabel: selectedMapping?.label,
            taxonomyCodeNodeType: selectedMapping?.nodeType,
            codeName: selectedMapping?.code,
            codeType: selectedMapping?.type,
            unitCost: selectedMapping?.unitCost,
            buyingQuantity: selectedMapping?.buyingQuantity,
            sellingQuantity: selectedMapping?.sellingQuantity,
            comments:
                commentsText !== null && commentsText !== ''
                    ? commentsText
                    : null,
        };

        dispatch(
            actions.daylight.addDaylightMapping(
                practiceId,
                partnerId,
                selectedCode,
                payload
            )
        ).then(() => {
            setSubmitting(false);
            closeModal();
        });
    };

    const closeModal = () => {
        setSelectedMapping(null);
        dispatch(actions.daylight.setActionTargetCode({}));
        dispatch(actions.daylight.setActionTargetMapping({}));
        dispatch(actions.daylight.setAddMappingModalOpen(false));
    };

    return (
        <Modal
            centered={true}
            isOpen={isOpen}
            className="text-light"
            style={{ minWidth: '50vw' }}>
            <ModalHeader>
                {`Add Mapping to ${selectedCode?.code} - "${selectedCode?.codeDescription}"`}
            </ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label>Mapping:</Label>
                    <Select
                        className="text-dark"
                        options={filteredOptions}
                        onChange={(e) => {
                            setSelectedMapping(e?.value);
                        }}
                        onInputChange={handleInputChange}
                        filterOption={() => true} // disable native filter
                        isClearable={true}
                    />
                </FormGroup>
                {canWriteComments && (
                    <FormGroup className="mt-3">
                        <Label>{'Comments (Optional):'}</Label>
                        <Input
                            type="textarea"
                            rows="8"
                            value={commentsText}
                            maxLength={800}
                            onChange={(e) => setCommentsText(e.target.value)}
                        />
                    </FormGroup>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="info" onClick={closeModal}>
                    Cancel
                </Button>
                <Button
                    color="success"
                    disabled={!selectedMapping || submitting}
                    onClick={confirm}>
                    Add{submitting ? 'ing' : ''}
                </Button>
            </ModalFooter>
        </Modal>
    );
};
const mappingsEquality = (newList, oldList, isOpen) => {
    if (!isOpen) return true;
    if (!newList || !oldList) return false;
    if (newList?.length !== oldList?.length) return false;
    if (newList?.length === 0 && oldList?.length === 0) return true;

    const listEqualityCheck = (oldEntity) => {
        const compareEntities = (newEntity) => {
            return shallowEqual(oldEntity, newEntity);
        };
        const check = newList?.find(compareEntities);
        return check !== undefined;
    };
    const isSamePimsList = oldList?.every(listEqualityCheck);
    return isSamePimsList;
};

export default DaylightAddMappingModal;
